import { addHours } from "date-fns";
import { useCallback, useMemo } from "react";
import { toast } from "sonner";
import { useMutation } from "urql";

import { SearchCommentatorInput } from "@/components/SearchInput";
import { PollInput, PollT } from "@/components/inputs/PollInput/PollInput";
import { isUpdatePollOption } from "@/components/inputs/PollInput/utils";
import { VideoInput } from "@/components/inputs/VideoInput";
import { Button } from "@/components/ui/button";
import { FragmentType, getFragmentData, graphql } from "@/gen";
import { AddPollInput, UpdatePollInput } from "@/gen/graphql";
import { useFormFields } from "@/hooks/useForm";
import { CommentatorT } from "@/hooks/useSearchCommentators";
import { useAddPoll, useUpdatePoll } from "@/routes/game-feed/query";
import { reportError } from "@/utils/errors";
import { useLanguageCodeWithDefault } from "@/utils/language";

import ChessPieceSelectorInput from "./ChessPieceSelectorInput";

const GameReportForm_GameReport = graphql(`
    fragment GameReportForm_GameReport on GameReport {
        id
        mostValuablePiece
        pieceOfShit
        pieceOfShitDescription
        mostValuablePieceDescription
        commentator {
            id
            displayName
        }
        pregamePoll {
            id
            title
            endAt
            options {
                id
                text
            }
        }
        recapVideoId
    }
`);

export const GameReportForm = ({
    gameId,
    gameReportFragment,
}: {
    gameId: string;
    gameReportFragment: FragmentType<typeof GameReportForm_GameReport> | null;
}) => {
    const gameReport = getFragmentData(GameReportForm_GameReport, gameReportFragment);

    const {
        pieceOfShit,
        pieceOfShitDescription,
        mostValuablePiece,
        mostValuablePieceDescription,
        commentator,
        pregamePoll,
        recapVideoId,
    } = gameReport ?? {
        pieceOfShit: null,
        pieceOfShitDescription: null,
        mostValuablePiece: null,
        mostValuablePieceDescription: null,
        commentator: null,
        pregamePoll: null,
        recapVideoId: null,
    };

    const form = useFormFields<{
        pieceOfShit: string | null;
        pieceOfShitDescription: string | null;
        mostValuablePiece: string | null;
        mostValuablePieceDescription: string | null;
        commentator: CommentatorT | null;
        pregamePoll: PollT | null;
        recapVideoUploadState: {
            videoId: string | null;
            progress: number;
        } | null;
    }>({
        pieceOfShit,
        pieceOfShitDescription,
        mostValuablePiece,
        mostValuablePieceDescription,
        commentator,
        pregamePoll,
        recapVideoUploadState: recapVideoId
            ? {
                  videoId: recapVideoId,
                  progress: recapVideoId ? 100 : 0,
              }
            : null,
    });

    const languageCode = useLanguageCodeWithDefault();
    const [{ error: _errorUpdateGame }, upsertGameReport] = useMutation(
        graphql(`
            mutation upsertGameReport($input: UpsertGameReportInput!) {
                upsertGameReport(input: $input) {
                    id
                }
            }
        `)
    );

    const [{ error: _errorAddPoll }, addPoll] = useAddPoll();
    const [{ error: _errorUpdatePoll }, updatePoll] = useUpdatePoll();

    const hasChanges = useMemo(() => {
        return (
            form.values.pieceOfShit !== pieceOfShit ||
            form.values.pieceOfShitDescription !== pieceOfShitDescription ||
            form.values.mostValuablePiece !== mostValuablePiece ||
            form.values.mostValuablePieceDescription !== mostValuablePieceDescription ||
            form.values.commentator?.id !== commentator?.id ||
            form.values.pregamePoll !== null || // TODO: Diff this properly
            form.values.recapVideoUploadState?.videoId !== recapVideoId
        );
    }, [
        form.values.pieceOfShit,
        form.values.pieceOfShitDescription,
        form.values.mostValuablePiece,
        form.values.mostValuablePieceDescription,
        form.values.commentator?.id,
        form.values.pregamePoll,
        form.values.recapVideoUploadState?.videoId,
        pieceOfShit,
        pieceOfShitDescription,
        mostValuablePiece,
        mostValuablePieceDescription,
        commentator?.id,
        recapVideoId,
    ]);

    const onSave = useCallback(async () => {
        if (
            form.values.recapVideoUploadState?.videoId &&
            form.values.recapVideoUploadState?.progress !== 100
        ) {
            toast.warning("Please wait for the recap video to finish uploading.");
            return;
        }

        let pollId = null;
        if (form.values.pregamePoll) {
            if (pregamePoll) {
                const updatePollResult = await updatePoll({
                    input: {
                        id: pregamePoll.id,
                        title: form.values.pregamePoll.title,
                        endAt: form.values.pregamePoll.endAt,
                        options: form.values.pregamePoll.options.map(option => {
                            if (isUpdatePollOption(option)) {
                                return {
                                    id: option.id,
                                    text: option.text,
                                } satisfies UpdatePollInput["options"][number];
                            }
                            return {
                                text: option.text,
                            };
                        }),
                    },
                });

                if (updatePollResult.error) {
                    toast.error("Unable to update poll", {
                        description: updatePollResult.error.message,
                    });
                    return;
                }
                pollId = updatePollResult.data?.updatePoll.id ?? null;
            } else {
                const addPollResult = await addPoll({
                    input: form.values.pregamePoll,
                });

                if (addPollResult.error) {
                    toast.error("Unable to add poll", {
                        description: addPollResult.error.message,
                    });
                    return;
                }

                pollId = addPollResult.data?.addPoll.id ?? null;
            }
        }

        try {
            await upsertGameReport({
                input: {
                    gameId: gameId,
                    languageCode: languageCode,
                    pieceOfShit: form.values.pieceOfShit,
                    pieceOfShitDescription: form.values.pieceOfShitDescription,
                    mostValuablePiece: form.values.mostValuablePiece,
                    mostValuablePieceDescription: form.values.mostValuablePieceDescription,
                    commentatorId: form.values.commentator?.id,
                    pregamePollId: pollId,
                    recapVideoId: form.values.recapVideoUploadState?.videoId,
                },
            });
            // TODO: Update the player in the cache instead of reloading the page
            window.location.reload();
        } catch (e) {
            reportError(e, "Error updating game");
        }
    }, [
        addPoll,
        form.values.commentator?.id,
        form.values.mostValuablePiece,
        form.values.mostValuablePieceDescription,
        form.values.pieceOfShit,
        form.values.pieceOfShitDescription,
        form.values.pregamePoll,
        form.values.recapVideoUploadState,
        gameId,
        languageCode,
        pregamePoll,
        updatePoll,
        upsertGameReport,
    ]);

    return (
        <div className="w-full space-y-3 sm:w-1/2">
            <SearchCommentatorInput
                label={'Commentator (displayed as "Covered by 😴" in the app)'}
                commentator={form.values.commentator}
                onCommentatorChange={commentator =>
                    form.setValue("commentator", commentator ?? null)
                }
            />
            <PollInput
                label={"Pregame poll"}
                poll={
                    form.values.pregamePoll
                        ? form.values.pregamePoll
                        : ({
                              title: "",
                              options: [],
                              endAt: addHours(new Date(), 2).toISOString(),
                          } as AddPollInput)
                }
                setPoll={poll => form.setValue("pregamePoll", poll)}
            />
            <VideoInput
                className={"w-1/4"}
                label="Recap video"
                value={form.values.recapVideoUploadState ?? { videoId: null, progress: 0 }}
                setValue={value => form.setValue("recapVideoUploadState", value)}
            />
            <div className="flex justify-between space-x-4">
                <div className="w-full rounded-xl bg-white p-2 sm:w-1/2 sm:p-8">
                    <div className="mb-4">
                        <label className="text-sm font-bold sm:text-base">💩 Piece of shit</label>
                    </div>
                    <ChessPieceSelectorInput
                        selected={form.values.pieceOfShit}
                        setSelected={sel => form.setValue("pieceOfShit", sel)}
                        description={form.values.pieceOfShitDescription ?? ""}
                        setDescription={desc => form.setValue("pieceOfShitDescription", desc)}
                    />
                </div>
                <div className="w-full rounded-xl bg-white p-2 sm:w-1/2 sm:p-8">
                    <div className="mb-4">
                        <label className="text-sm font-bold sm:text-base">
                            🥇 Most valuable piece
                        </label>
                    </div>

                    <ChessPieceSelectorInput
                        selected={form.values.mostValuablePiece}
                        setSelected={sel => form.setValue("mostValuablePiece", sel)}
                        description={form.values.mostValuablePieceDescription ?? ""}
                        setDescription={desc => form.setValue("mostValuablePieceDescription", desc)}
                    />
                </div>
            </div>
            <div className="flex flex-row justify-between">
                <div className="flex space-x-2">
                    <Button variant="secondary" onClick={() => form.reset()}>
                        Reset
                    </Button>
                    <Button onClick={onSave} disabled={!hasChanges}>
                        Save
                    </Button>
                </div>
            </div>
        </div>
    );
};
